/**
 * Reassign a ticket with a reason (16 Oct). The reason (pick-list, optional
 * text; text needed for "Other") is written to the ticket as an internal
 * journal note, "Reassigned: A → B: reason", and into the routing history.
 */

import { Alert, Button, Field, Textarea, Typography } from "@norican/react";
import { type FormEvent, useState } from "react";

import { ErrorState, SectionCard } from "@/components/common";
import { OptionSelect } from "@/features/knowledge/shared";
import { useT } from "@/i18n";
import type { AssignTicket, ReassignResponse } from "@/lib/api";

import { useReassign } from "./api";
import { REASSIGN_REASONS, type ReassignReasonCode } from "./lib";
import { TeamPersonFields, type TeamPersonValue, useChosen } from "./team-person-fields";

export function ReassignCard({
  ticket,
  initial,
  onDone,
}: {
  ticket: AssignTicket;
  initial?: TeamPersonValue;
  onDone?: (result: ReassignResponse) => void;
}) {
  const { t } = useT("tickets");
  const reassign = useReassign(ticket.ticket_id);
  const [value, setValue] = useState<TeamPersonValue>(initial ?? { teamId: "", personId: "" });
  const [reason, setReason] = useState<ReassignReasonCode | "">("");
  const [note, setNote] = useState("");
  const { team } = useChosen(value);
  const noteMissing = reason === "other" && note.trim() === "";
  const same =
    team !== null &&
    team.name.toLowerCase() === (ticket.team ?? "").toLowerCase() &&
    value.personId === "";

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (value.teamId === "" || reason === "" || noteMissing || same) {
      return;
    }
    reassign.mutate(
      {
        team_id: value.teamId,
        owner_id: value.personId === "" ? null : value.personId,
        reason,
        note: note.trim() === "" ? null : note.trim(),
      },
      { onSuccess: (result) => onDone?.(result) },
    );
  }

  const done = reassign.data;
  return (
    <SectionCard title={t("itAdmin.assign.reassign.title")}>
      <form onSubmit={submit} className="flex flex-col gap-3">
        <Typography variant="body-sm" tone="muted">
          {t("itAdmin.assign.reassign.intro")}
        </Typography>
        {reassign.error == null ? null : <ErrorState error={reassign.error} variant="inline" />}
        {done ? (
          <Alert tone={done.note_failed || done.owner_not_set ? "warning" : "success"}>
            {t("itAdmin.assign.reassign.done", { team: done.team })}
            {done.owner_not_set ? ` ${t("itAdmin.assign.reassign.ownerNotSet")}` : ""}
            {done.note_failed ? ` ${t("itAdmin.assign.reassign.noteFailed")}` : ""}
          </Alert>
        ) : null}
        <TeamPersonFields
          idPrefix="reassign"
          value={value}
          onChange={setValue}
          personLabel={t("itAdmin.assign.reassign.owner")}
          personNone={t("itAdmin.assign.reassign.ownerNone")}
        />
        {same ? (
          <Typography variant="caption" tone="danger">
            {t("itAdmin.assign.reassign.same")}
          </Typography>
        ) : null}
        <Field name="reassign-reason" label={t("itAdmin.assign.reassign.reasonLabel")} required>
          <OptionSelect
            options={REASSIGN_REASONS.map((code) => ({
              value: code,
              label: t(`itAdmin.assign.reassign.reason.${code}`),
            }))}
            value={reason}
            onValueChange={(next) => setReason(next as ReassignReasonCode | "")}
            placeholder={t("itAdmin.assign.reassign.pickReason")}
            aria-label={t("itAdmin.assign.reassign.reasonLabel")}
          />
        </Field>
        <Field
          name="reassign-note"
          label={t("itAdmin.assign.reassign.note")}
          {...(noteMissing ? { error: t("itAdmin.assign.reassign.noteRequired") } : {})}
        >
          <Textarea
            name="reassign-note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={2}
            autoResize
            maxLength={1000}
          />
        </Field>
        <div>
          <Button
            type="submit"
            loading={reassign.isPending}
            disabled={value.teamId === "" || reason === "" || noteMissing || same}
          >
            {t("itAdmin.assign.reassign.submit")}
          </Button>
        </div>
      </form>
    </SectionCard>
  );
}
